import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';

type ToastType = 'success' | 'error' | 'info';
interface ToastItem { id: number; message: string; type: ToastType; }

let counter = 0;

/** <cadence-toast> — a singleton notification stack. Use showToast() to trigger. */
@customElement('cadence-toast')
export class CadenceToast extends LitElement {
  @state() private toasts: ToastItem[] = [];

  static styles = css`
    :host {
      position: fixed; bottom: 20px; right: 20px; z-index: 1000;
      display: flex; flex-direction: column; gap: 10px; max-width: min(360px, calc(100vw - 40px));
    }
    .toast {
      padding: 12px 16px; border-radius: var(--radius-md); color: #fff;
      box-shadow: var(--shadow-lg); font-size: 0.9rem;
      animation: slide-in 0.2s ease-out;
    }
    .success { background: var(--color-success); }
    .error { background: var(--color-danger); }
    .info { background: var(--color-text-primary); }
    @keyframes slide-in { from { transform: translateY(8px); opacity: 0; } to { transform: none; opacity: 1; } }
  `;

  show(message: string, type: ToastType = 'info') {
    const id = ++counter;
    this.toasts = [...this.toasts, { id, message, type }];
    setTimeout(() => this.dismiss(id), 4000);
  }

  private dismiss(id: number) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }

  render() {
    return html`
      ${this.toasts.map(
        (toast) => html`
          <div class="toast ${toast.type}" role="status" @click=${() => this.dismiss(toast.id)}>
            ${toast.message}
          </div>
        `,
      )}
    `;
  }
}

export function showToast(message: string, type: ToastType = 'info'): void {
  let el = document.querySelector('cadence-toast') as CadenceToast | null;
  if (!el) {
    el = document.createElement('cadence-toast') as CadenceToast;
    document.body.appendChild(el);
  }
  el.show(message, type);
}
